
import './assets/styles/tailwind.scss';
import './assets/styles/index.scss';
import './assets/styles/formulate.scss';

import { MountingPortal } from 'portal-vue';
import { Plugin as Fragment } from 'vue-fragment'
import VueGtag from 'vue-gtag';
import Intersect from 'vue-intersect'
import Meta from 'vue-meta'
import { Component, ProvideReactive, Vue, Watch } from 'vue-property-decorator';
import { Route } from 'vue-router';
import VueScreen from 'vue-screen';
import Drawer from 'vue-simple-drawer';
import VueCollapse from 'vue2-collapse'; // https://roszpun.github.io/vue-collapse/#/
import { Action, Getter, Mutation } from 'vuex-class';

import ApplyPromotions from '@/components/ApplyPromotions.vue';
import ApplyTip from '@/components/ApplyTip.vue';
import ArrowSvg from '@/components/ArrowSvg.vue';
import Back from '@/components/Back.vue';
import BackSvg from '@/components/BackSvg.vue';
import Button from '@/components/Button.vue';
import Cart from '@/components/Cart.vue';
import Checkbox from '@/components/Checkbox.vue';
import CheckmarkDot from '@/components/CheckmarkDot.vue';
import CheckmarkSvg from '@/components/CheckmarkSvg.vue';
import FavoriteOrder from '@/components/FavoriteOrder.vue';
import GoogleAutocomplete from '@/components/GoogleAutocomplete.vue';
import Location from '@/components/Location.vue';
import LogoutModal from '@/components/LogoutModal.vue';
import Map from '@/components/Map.vue';
import MapPinSvg from '@/components/MapPinSvg.vue';
import OrderHeader from '@/components/OrderHeader.vue';
import OrderLocationDetails from '@/components/OrderLocationDetails.vue';
import OrderTotals from '@/components/OrderTotals.vue';
import ProductQuantity from '@/components/ProductQuantity.vue';
import ProductsSummary from '@/components/ProductsSummary.vue';
import RadioButton from '@/components/RadioButton.vue';
import Rewards from '@/components/Rewards.vue';
import SandwichSvg from '@/components/SandwichSvg.vue';
import Select from '@/components/Select.vue';
import SelectLocationAndTimeDetails from '@/components/SelectLocationAndTimeDetails.vue';
import SingleProduct from '@/components/SingleProduct.vue';
import SpinnerSvg from '@/components/SpinnerSvg.vue';
import { MutationTypes, OrderSteps, Views } from '@/enums';
import BasketModule from '@/store/modules/basket';
import UserModule from '@/store/modules/user';
import LoadScript from '@/utils/load-script.util';
import OrderSummary from '@/views/OrderSummary.vue';

import { submitCategoryView, submitProductView } from './utils/events.utils';
import { hideCMSContent } from './utils/global.util';

declare global {
  interface Window {
    __INITIAL_STATE__: {
      user: User;
      categories: Category[];
      thankYouUrl: string;
      sweepstakesOptions: string;
      contactUsOptions: string;
      loyaltyRewards: LoyaltyAccountReward[];
      regionalPages: RegionPagePath[];
    };
  }
}

const { SET_ACTIVE_CATEGORY, SET_ACTIVE_PRODUCT, SET_ACTIVE_REGION_ID, SET_EDIT_IS_OPEN, SET_MENU, CLEAR_PRODUCT_MODIFIER_CACHE } = MutationTypes;

Vue.use(Fragment)
Vue.use(LoadScript);
Vue.use(Meta, { refreshOnceOnNavigation: true })
Vue.use(VueCollapse)
Vue.use(VueScreen);

Vue.use(VueGtag, {
  config: {
    id: 'UA-2556202-2'
  }
});

Vue.component('arrow-svg', ArrowSvg);
Vue.component('back-svg', BackSvg);
Vue.component('back', Back);
Vue.component('google-map', Map);
Vue.component('map-pin-svg', MapPinSvg);
Vue.component('checkmark-svg', CheckmarkSvg);
Vue.component('sandwich-svg', SandwichSvg);
Vue.component('checkmark-dot', CheckmarkDot);
Vue.component('intersect', Intersect);
Vue.component('vue-button', Button);
Vue.component('vue-checkbox', Checkbox);
Vue.component('vue-radio', RadioButton);
Vue.component('vue-select', Select);
Vue.component('google-autocomplete', GoogleAutocomplete);
Vue.component('spinner-svg', SpinnerSvg);
Vue.component('rewards', Rewards);
Vue.component('cart', Cart);
Vue.component('drawer', Drawer);
Vue.component('favorite-order', FavoriteOrder);
Vue.component('single-product', SingleProduct);
Vue.component('apply-tip', ApplyTip);
Vue.component('order-location-details', OrderLocationDetails);
Vue.component('select-location-and-time-details', SelectLocationAndTimeDetails);
Vue.component('order-totals', OrderTotals);
Vue.component('products-summary', ProductsSummary);
Vue.component('product-quantity', ProductQuantity);
Vue.component('apply-promotions', ApplyPromotions);

@Component({
  components: {
    Location,
    OrderHeader,
    OrderSummary,
    MountingPortal
  }
})
export default class App extends Vue {
  @ProvideReactive('isMobile') isMobile = true;
  @ProvideReactive('progressRef') progressRef;
  @Getter activeCategory!: Category;
  @Getter activeProduct!: Product;
  @Getter activeRestaurant!: Restaurant;
  @Getter activeRegionId: string;
  @Getter categories!: Category[];
  @Getter editIsOpen: boolean;
  @Mutation(SET_ACTIVE_CATEGORY) setActiveCategory: (category: Category | null) => void;
  @Mutation(SET_ACTIVE_REGION_ID) setActiveRegionId: (regionId: string) => void;
  @Mutation(SET_ACTIVE_PRODUCT) setActiveProduct: (product: Product) => void;
  @Mutation(SET_EDIT_IS_OPEN) setEditIsOpen: (action: boolean) => void;
  @Mutation(SET_MENU) setMenuFromState: (categories: Category[]) => void;
  @Mutation(CLEAR_PRODUCT_MODIFIER_CACHE) clearProductModifierCache: () => void;
  @Action fetchRestaurant: ({ restaurantId }: { restaurantId: number; fetchFromState: boolean }) => void;
  @Action fetchRestaurantMenu: ({ slug, fetchFromState }: { slug: string; fetchFromState: boolean }) => void;
  @Action fetchRegionIdFromCoords: (coords: Coords) => Promise<string>;

  reviewDrawerOpen: boolean;
  loginDrawerOpen: boolean;

  formModel: {
    userName: string;
    password: string;
    rememberMe: boolean;
    recaptchaToken: string;
  };

  data() {
    return {
      reviewDrawerOpen: false,
      loginDrawerOpen: false,
      formModel: {
        username: '',
        password: '',
        rememberMe: false,
        recaptchaToken: ''
      }
    }
  }

  @Watch('$route', { immediate: true, deep: true })
  onUrlChange(route: Route, oldRoute: Route) {
    if (oldRoute) {
      // eslint-disable-next-line @typescript-eslint/camelcase
      this.$gtag.pageview(route as any)
    }
  }

  get orderSteps() {
    return Object.values(OrderSteps || {});
  }

  get stepInfo(): OrderStep {
    const routeName = this.$route?.name;
    return (this.orderSteps?.find(({ view, aliasViews = [] }) => {
      return view === routeName || aliasViews?.includes(routeName as string);
    })) || {} as OrderStep;
  }

  get hideMainApp() {
    const routeName = this.$route?.name;
    return routeName === null ||
      routeName === 'ContactUsForm' ||
      routeName === 'SweepstakesForm' ||
      routeName === 'WSAForm' ||
      routeName === 'FreeSubsForm' ||
      routeName === 'OrderHistory' ||
      routeName === 'RewardHistory' ||
      routeName === 'Rewards' ||
      routeName === 'FavoriteLocations' ||
      routeName === 'FoundationApplicationForm';
  }

  get hasBasket() {
    return BasketModule.id;
  }

  async created() {
    this.setEditIsOpen(false);
    this.clearProductModifierCache();
    if (this.activeRestaurant?.id && this.activeRestaurant?.slug && !this.hideMainApp) {
      await this.fetchRestaurant({ restaurantId: this.activeRestaurant.id, fetchFromState: false });
      await this.fetchRestaurantMenu({ slug: this.activeRestaurant.slug, fetchFromState: false });
    }

    if (BasketModule?.id !== null && BasketModule.id !== '' && !this.hideMainApp && this.stepInfo.step !== undefined) {
      const loader = this.$loading.show();
      try {
        // Call get basket when the app is created to make sure it exists.
        await BasketModule.getBasket(BasketModule.id);

        if (UserModule.loggedIn) {
          UserModule.getBillingAccounts(BasketModule.id);
          UserModule.getRewards(this.activeRestaurant.id);
        }

        if (this.stepInfo.step === 1) {
          this.$router.push({
            name: 'ChooseFood',
            params: {
              ...this.$route.params,
              location: this.activeRestaurant.slug,
            }
          });
        }
      } catch (error) {
        // If basket does not exist, set to null and start flow over
        BasketModule.setBasket(null);
        this.$toasted.error('Your basket has expired.');
        this.$log.info('Basket does not exist.');
        this.$router.push(
          {
            name: 'SelectLocation',
            params: {
              ...this.$route.params
            }
          });
      } finally {
        loader.hide();
      }
    }
    Vue.loadScript('https://kit.fontawesome.com/152b040dc0.js');

    if (!this.showRecaptcha) {
      await this.$recaptchaLoaded();
      const recaptcha = this.$recaptchaInstance
      if (recaptcha) {
        recaptcha.hideBadge();
      }
    }
  }

  get showRecaptcha() {
    const routeName = this.$route?.name;
    const path = this.$route?.path;
    return routeName === 'ContactUsForm' ||
      routeName === 'SweepstakesForm' ||
      routeName === 'FreeSubsForm' ||
      routeName === 'FoundationApplicationForm' ||
      routeName === 'WSAForm' ||
      path.toLowerCase() === '/login' ||
      path.toLowerCase() === '/register' ||
      path.toLowerCase() === '/account/forgotpassword';
  }

  toggleReviewDrawer() {
    this.loginDrawerOpen = false;
    this.reviewDrawerOpen = !this.reviewDrawerOpen;
  }

  async toggleLoginDrawer() {
    this.reviewDrawerOpen = false;
    this.loginDrawerOpen = !this.loginDrawerOpen;
    await this.$recaptchaLoaded();
    const recaptcha = this.$recaptchaInstance
    if (recaptcha) {
      if (this.loginDrawerOpen) {
        recaptcha.showBadge();
      } else {
        recaptcha.hideBadge();
      }
    }

    const menu = document.getElementById('mobileMenu');
    if (menu && menu.classList.contains('opacity-100')) {
      const button = document.getElementById('mobile-menu-toggle');
      if (button) {
        button.click();
      }
    }
  }

  @Watch('$screen', { immediate: true, deep: true })
  onScreenChange(oldValue, newValue) {
    const useValues = newValue || oldValue;
    const lastMobBreakpoint = useValues?.breakpointsOrder?.indexOf('md');
    const currIndex = useValues?.breakpointsOrder?.indexOf(useValues.breakpoint);
    this.isMobile = lastMobBreakpoint >= currIndex;
  }

  @Watch('activeCategory')
  async handleActiveCategoryChange(newValue, oldValue) {
    if (newValue?.id && newValue.id !== oldValue?.id &&
      this.$route.name === Views.CHOOSE_FOOD_CATEGORY) {
      submitCategoryView(newValue.id);
    }
  }

  @Watch('activeProduct')
  async handleActiveProductChange(newValue, oldValue) {
    if (newValue?.id && newValue.id !== oldValue?.id &&
      this.$route.name === Views.PRODUCT_ADD) {
      submitProductView(newValue.id);
    }
  }

  async mounted() {
    this.progressRef = this.$refs.progress;

    window.addEventListener('DOMContentLoaded', async () => {
      const user = window?.__INITIAL_STATE__?.user;
      if (user) {
        UserModule.setUser(user);
      }

      const loyaltyRewards = window?.__INITIAL_STATE__?.loyaltyRewards;
      if (loyaltyRewards) {
        UserModule.setLoyaltyRewards(loyaltyRewards);
      }
      const regionalPages = window?.__INITIAL_STATE__?.regionalPages as RegionPagePath[];
      if (regionalPages) {
        let regionId = this.activeRegionId || this.activeRestaurant?.regionId;
        if (!regionId) {
          let latitude;
          let longitude;
          if (!UserModule.customerLocation.hasCoords) {
            if (this.activeRestaurant?.latitude && this.activeRestaurant?.longitude) {
              latitude = this.activeRestaurant.latitude;
              longitude = this.activeRestaurant.longitude;
              regionId = await this.fetchRegionIdFromCoords({ latitude, longitude } as Coords);
            } else {
              try {
                regionId = await new Promise((resolve, reject) => {
                  navigator.geolocation.getCurrentPosition(async (position) => {
                    latitude = position?.coords?.latitude;
                    longitude = position?.coords?.longitude;
                    const fetchedRegionId = await this.fetchRegionIdFromCoords({ latitude, longitude } as Coords);
                    UserModule.setLocation({
                      latitude,
                      locality: '',
                      longitude,
                      postal_code: '',
                      route: '',
                      street_number: '',
                      placeInfo: {
                        adr_address: '',
                        formatted_address: '',
                        name: '',
                        place_id: '',
                        url: '',
                      }
                    });
                    resolve(fetchedRegionId);
                  }, reject);
                });
              } catch (error) {
                console.error(error);
              }
            }
          } else {
            latitude = UserModule.customerLocation.coords.latitude;
            longitude = UserModule.customerLocation.coords.longitude;
            regionId = await this.fetchRegionIdFromCoords({ latitude, longitude } as Coords);
          }
        }
        if (regionId) {
          this.setActiveRegionId(regionId);
          const regionPage = regionalPages.find((region) => region.id === regionId);
          if (regionPage && this.hideMainApp) {
            window.location.href = regionPage.path;
          }
        }
      }
    });

    if (!this.hideMainApp) {
      hideCMSContent();
    }
  }

  handleChooseCategory({ category, callback }: { category: Category; callback: any }) {
    this.setActiveCategory(category);

    this.$router.push({
      name: 'ChooseFoodCategory',
      params: {
        ...this.$route.params,
        categorySlug: category?.slug
      },
    }).then(() => {
      if (category.products?.length === 1) {
        return this.handleChooseProduct(category.products[0]);
      }
    });

    if (callback) {
      setTimeout(() => callback(), 500);
    }
  }

  handleChooseProduct(product: Product) {
    this.$router.push({
      name: 'ProductAdd',
      params: {
        ...this.$route.params,
        categorySlug: this.activeCategory?.slug,
        productSlug: product?.slug,
      },
    });
  }

  handleToggleEdit(action: boolean) {
    this.setEditIsOpen(action);
    this.$scrollTo(this.$refs.progress as Element);
  }

  handleActionComplete(payload: { action: string; data: any}) {
    if (payload.action === 'toggleCollapse' && typeof payload.data === 'boolean') {
      setTimeout(() => {
        this.handleToggleEdit(payload.data);
      }, 0)
    }
  }

  fitMapToBounds() {
    this.mapRef.fitBounds();
  }

  get mapRef() {
    return (this.$refs.my_map || {} as any);
  }

  get collapseClass() {
    return {
      active: this.editIsOpen
    }
  }

  get showHeaderIfViewExist() {
    return this.stepInfo.step !== undefined;
  }

  get appClasses() {
    if (this.hideMainApp) {
      return '';
    }
    return 'mt-1/50 lg:mt-1/20 pb-8';
  }

  async handleLogin() {
    const loader = this.$loading.show();
    try {
      await this.$recaptchaLoaded()
      const token = await this.$recaptcha('login');
      const form = document.getElementById('login-form') as HTMLFormElement;
      if (form) {
        const userNameInput = document.getElementById('LoginUserName') as HTMLInputElement;
        const passwordInput = document.getElementById('LoginPassword') as HTMLInputElement;
        const rememberMeInput = document.getElementById('LoginRememberMe') as HTMLInputElement;
        const returnUrlInput = document.getElementById('LoginReturnUrl') as HTMLInputElement;
        const recaptchaInput = document.getElementById('LoginRecaptchaToken') as HTMLInputElement;
        userNameInput.value = this.formModel.userName;
        passwordInput.value = this.formModel.password;
        rememberMeInput.value = this.formModel.rememberMe.toString();
        returnUrlInput.value = this.$route.fullPath;
        recaptchaInput.value = token;
        form.submit();
      }
    } catch (err) {
      this.$log.error(err);
      loader.hide();
      this.$toasted.error(err.data);
    }
  }

  handleLogOut() {
    try {
      this.$modal.show(
        LogoutModal, {
        }, {
          clickToClose: false,
          scrollable: true,
          focusTrap: true,
          height: 'auto',
          width: this.isMobile ? '90%' : '60%',
        }, {
          'before-close': this.handleModalResponse
        }
      );
    } catch (err) {
      this.$log.error(err);
      this.$toasted.error(err.data);
    }
  }

  handleModalResponse(input) {
    if (input.params === 'logout') {
      const form = document.getElementById('logout-form') as HTMLFormElement;
      if (form) {
        BasketModule.setBasket({});
        form.submit();
      }
    }
  }

  get isLoggedIn() {
    return UserModule.loggedIn;
  }
}
